import { DEFAULT_CURRENCY_CODE, formatCurrency, roundHalfUpCents } from '../money';
import { dealValueFloorCents, lineCostCents, lineFloorCents } from './dealPricing';
import { DEAL_STAGES, SUB_STAGE_SEED, type DealStage, type SubStage, type SubStageDefinitionSeed } from './dealModel';

/**
 * The Deal page's derived drawings (piece 19): Deal Progress as a checklist
 * off the same gate data as the stages, the NOW pill on the current stage and
 * the red ring with the STALE pill at threshold, the StageCard's counter, Next
 * Action in due order with N/A rows absent, the floor's own arithmetic, and
 * the board's columns. 🔨 Nothing here is a stored progress field, and no
 * acknowledge-and-keep-waiting state exists (decisions 0206, 0048).
 */
export const PROGRESS_STAGES: readonly DealStage[] = ['Intent', 'Discovery', 'Evaluation', 'Negotiation', 'Decision', 'Won'];

/** Stage n of 6 on the banner since Discovery joined (decision 5450); Lost is off the ladder and answers 0. */
export function stageOrdinal(stage: DealStage): number {
  return PROGRESS_STAGES.indexOf(stage) + 1;
}

export type ProgressState = 'done' | 'now' | 'stale' | 'todo' | 'lost';

export interface ProgressSubStageInput {
  subStage: SubStage;
  applicable: boolean;
  completedAt?: Date | string | null;
  dueDate?: Date | string | null;
}

export interface StagnationInput {
  subStage: SubStage | null;
  days: number;
  stale: boolean;
  escalate: boolean;
}

export interface ProgressSubStageRow {
  subStage: SubStage;
  shortName: string;
  applicable: boolean;
  completed: boolean;
  state: 'done' | 'now' | 'stale' | 'todo' | 'na';
  days: number | null;
  escalate: boolean;
  /**
   * 🔒 The sub-stage's Due as a date, which the ladder draws beside an open row
   * (decision 1215). Null where none is set and on a stage whose rows take none,
   * so an Intent row's leftover date is never drawn (decision 0719).
   */
  dueDate: string | null;
}

export interface ProgressStageRow {
  stage: DealStage;
  ordinal: number;
  state: ProgressState;
  /** The stage's own stagnation, Decision's stage-level count among them. */
  days: number | null;
  escalate: boolean;
  subStages: ProgressSubStageRow[];
}

/**
 * The checklist: completed stages ticked (a stage behind the Deal with an open
 * applicable row stays an empty ring, as its card counts it), the current stage a hollow ring with
 * NOW, or a red ring with STALE once any of its applicable rows or its own
 * stage-level count is past threshold; remaining stages empty rings. A Lost
 * Deal draws its ladder as it stood, with no ring and no stagnation.
 */
export function progressRows(input: { stage: DealStage; subStages: readonly ProgressSubStageInput[]; stagnation: readonly StagnationInput[]; definitions?: readonly SubStageDefinitionSeed[] }): ProgressStageRow[] {
  const definitions = input.definitions ?? SUB_STAGE_SEED;
  const current = stageOrdinal(input.stage);
  return PROGRESS_STAGES.map((stage) => {
    const ordinal = stageOrdinal(stage);
    const rows = definitions
      .filter((d) => d.stage === stage && d.subStage !== null)
      .map((d): ProgressSubStageRow => {
        const row = input.subStages.find((s) => s.subStage === d.subStage);
        const verdict = input.stagnation.find((v) => v.subStage === d.subStage);
        const applicable = row?.applicable ?? false;
        const completed = Boolean(row?.completedAt);
        const isCurrent = stage === input.stage;
        const state = !applicable ? 'na' : completed ? 'done' : verdict?.stale ? 'stale' : isCurrent ? 'now' : 'todo';
        const due = row?.dueDate ? (row.dueDate instanceof Date ? row.dueDate.toISOString() : String(row.dueDate)).slice(0, 10) : null;
        return { subStage: d.subStage!, shortName: d.shortName, applicable, completed, state, days: verdict?.days ?? null, escalate: verdict?.escalate ?? false, dueDate: stageTakesDueDates(stage) ? due : null };
      });
    // 🔒 Only the current stage's first open action takes the Now pill; the later open rows read as to do (T977; decision 10690).
    const firstOpen = rows.findIndex((r) => r.applicable && !r.completed);
    rows.forEach((r, i) => { if (r.state === 'now' && i !== firstOpen) r.state = 'todo'; });
    const stageLevel = stage === input.stage ? input.stagnation.find((v) => v.subStage === null) : undefined;
    const anyStale = stage === input.stage && (rows.some((r) => r.state === 'stale') || stageLevel?.stale === true);
    // 🔒 A stage behind the Deal ticks only once its card's applicable rows are all answered, so a Quick Deal's Intent, skipped with Authority and Need open, reads as its card does (T926, the data-entry snags; decision 1713).
    const answered = rows.every((r) => !r.applicable || r.completed);
    const state: ProgressState = input.stage === 'Lost' ? 'lost' : ordinal < current ? (answered ? 'done' : 'todo') : ordinal === current ? (anyStale ? 'stale' : 'now') : 'todo';
    const staleDays = anyStale ? Math.max(stageLevel?.stale ? stageLevel.days : 0, ...rows.filter((r) => r.state === 'stale').map((r) => r.days ?? 0)) : null;
    return { stage, ordinal, state, days: staleDays, escalate: stageLevel?.escalate === true || rows.some((r) => r.state === 'stale' && r.escalate), subStages: rows };
  });
}

/** The STALE pill's own text, the exact day count beside it. */
export function stalePillText(days: number): string {
  return `STALE — ${days}d`;
}

/** The card's progress counter: checked over applicable, so an N/A row never counts against the stage. */
export function cardCounter(rows: readonly { applicable: boolean; completed: boolean }[]): string {
  const applicable = rows.filter((r) => r.applicable);
  return `${applicable.filter((r) => r.completed).length}/${applicable.length}`;
}

/**
 * 🔒 The stages whose rows carry no Due of their own (decision 0719). Intent is
 * one meeting — the specs, the timeline, the budget and the people involved are
 * gathered together — so the card is measured by one Age and its four rows by
 * nothing. The other stages keep a Due per row, because their parallel
 * sub-stages have different deadlines (piece 18).
 */
export const UNDATED_STAGES: readonly DealStage[] = ['Intent'];

/** Whether a stage's rows take a Due date of their own (decision 0719). */
export function stageTakesDueDates(stage: DealStage): boolean {
  return !UNDATED_STAGES.includes(stage);
}

/**
 * 🔒 The Intent card's Age (decision 0719): whole days from the Deal entering
 * Intent, the Intent gate's own `enteredAt`. While the Deal is at Intent it
 * counts to now; once the Deal has moved on it stops at the day the next stage
 * was entered, Discovery or, on a Deal that never entered it, Evaluation
 * (decision 5450), so a finished Intent reads how long it took. Null where the Deal has
 * no Intent gate.
 */
export function intentAgeDays(gates: readonly { stage: string; enteredAt: Date | string }[], now: Date, currentStage: DealStage): number | null {
  const intent = gates.find((gate) => gate.stage === 'Intent');
  if (!intent) return null;
  const next = gates.find((gate) => gate.stage === 'Discovery') ?? gates.find((gate) => gate.stage === 'Evaluation');
  const end = currentStage === 'Intent' || !next ? now : new Date(next.enteredAt);
  return Math.max(0, Math.floor((end.getTime() - new Date(intent.enteredAt).getTime()) / 86_400_000));
}

/**
 * 🔨 Next Action lists only applicable, incomplete sub-stages of the current stage, soonest due first and undated last; N/A rows are absent.
 * 🔒 An Intent row's Due is never read (decision 0719): a date set before the card lost its Due column stays in the row and orders nothing.
 */
export function nextActionRows(stage: DealStage, rows: readonly ProgressSubStageInput[], definitions: readonly SubStageDefinitionSeed[] = SUB_STAGE_SEED): { subStage: SubStage; shortName: string; dueDate: string | null }[] {
  if (stage === 'Decision') return [{ subStage: 'IssueSalesOrder', shortName: 'Won or Lost', dueDate: null }];
  const time = (d: Date | string | null | undefined) => (d ? new Date(d).getTime() : Number.POSITIVE_INFINITY);
  return definitions
    .filter((d) => d.stage === stage && d.subStage !== null)
    .map((d) => ({ d, row: rows.find((r) => r.subStage === d.subStage) }))
    .filter(({ row }) => row !== undefined && row.applicable && !row.completedAt)
    .map(({ d, row }) => ({ d, due: stageTakesDueDates(d.stage) ? row!.dueDate ?? null : null }))
    .sort((a, b) => time(a.due) - time(b.due) || a.d.sortOrder - b.d.sortOrder)
    .map(({ d, due }) => ({ subStage: d.subStage!, shortName: d.shortName, dueDate: due ? new Date(due).toISOString().slice(0, 10) : null }));
}

export interface FloorLine {
  sku: string;
  quantity: number;
  purchasePrice: number;
  minimumMarginPercent: number;
}

const money = (n: number) => formatCurrency({ amount: roundHalfUpCents(n * 100), currencyCode: DEFAULT_CURRENCY_CODE });
const moneyCents = (amount: number) => formatCurrency({ amount, currencyCode: DEFAULT_CURRENCY_CODE });

/** The arithmetic the floor came from, one line per BOM line and a total, so an override is of a number the rep was shown (decision 0048). */
export function floorDerivation(lines: readonly FloorLine[]): string[] {
  const out = lines.map((line) => {
    const divisor = (1 - line.minimumMarginPercent / 100).toFixed(2);
    return `${line.sku}: ${line.quantity} × ${money(line.purchasePrice)} = ${moneyCents(lineCostCents(line))} ÷ ${divisor} = ${moneyCents(lineFloorCents(line))}`;
  });
  out.push(lines.length === 0 ? 'No BOM lines yet, so the floor is $0.00.' : `Floor: the sum of the line floors, ${moneyCents(dealValueFloorCents(lines))}.`);
  return out;
}

export interface BoardCard {
  id: string;
  name: string;
  customerName: string;
  /** The Deal's value in whole cents (T2192 item 12). */
  dealValueCents: number;
  ownerName: string;
  daysInStage: number;
  stale: boolean;
  staleDays: number | null;
  stage: DealStage;
  /** The card reads the type and the expected close beside the value and the owner (decision 0300). */
  dealType?: string;
  expectedCloseDate?: string | null;
  /** 🔒 The page the Deal draws (decision 2293), so the board refuses a Parts page's Deal a stage it never stands at (decision 2430). */
  dealPage?: 'full' | 'parts' | 'request';
}

/** A column's own total in whole cents, for its head (decision 0300). */
export function columnTotal(cards: readonly BoardCard[]): number {
  return cards.reduce((sum, card) => sum + card.dealValueCents, 0);
}

/** One column per top-level stage of the seeded set, in stage order; a column with no card still renders (decision 0206). */
export function boardColumns(cards: readonly BoardCard[]): { stage: DealStage; cards: BoardCard[] }[] {
  return DEAL_STAGES.map((stage) => ({ stage, cards: cards.filter((card) => card.stage === stage) }));
}

/** 🔨 A drop on another column opens the Deal at that gate and never moves the stage: the answer is a route, not a write. */
export function dropTarget(dealId: string, stage: DealStage): string {
  return `/deals/${dealId}?gate=${stage}`;
}

/** A board column narrower than this reads as a strip, not a column (decision 0327); the CSS token `--board-column-min` carries the same figure. */
export const BOARD_COLUMN_MIN_PX = 140;
/** The gap between board columns, the same figure as `--space-3`. */
export const BOARD_COLUMN_GAP_PX = 12;

/**
 * Whether the board's columns all fit side by side in `width` at their minimum
 * width. When they do not, the board stacks them top to bottom rather than
 * scrolling sideways (decision 0327).
 */
export function boardFits(width: number, columns: number): boolean {
  if (columns <= 0) return true;
  return width >= columns * BOARD_COLUMN_MIN_PX + (columns - 1) * BOARD_COLUMN_GAP_PX;
}
